import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Loader2, Send, EyeOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function SupplierBroadcasts() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/supplier/broadcasts");
      setRows(r.data || []);
    } catch (e) { toast.error("Could not load broadcasts"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const submitQuote = async (bid, form) => {
    try {
      await api.post(`/supplier/broadcasts/${bid}/quote`, form);
      toast.success("Quote submitted — awaiting Vinci review");
      setOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[1200px] mx-auto">
        <Link to="/account" data-testid="broadcasts-back" className="inline-flex items-center gap-2 text-[#8A8A82] hover:text-[#B87333] text-xs uppercase tracking-[0.2em] font-grotesk mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-white/10 pb-6">
          <div>
            <div className="overline text-[#B87333] mb-2">Anonymised RFQs</div>
            <h1 className="font-serif text-4xl md:text-5xl">Open Requirements</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              Buyer requirements curated and shared by the Vinci trade desk. Buyer identities are masked until you win the mandate.
              Submit competitive quotes — the trade desk reviews and forwards the strongest ones to the buyer.
            </p>
          </div>
          <button data-testid="broadcasts-refresh" onClick={load} className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#B87333] flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-[#8A8A82] font-body text-sm border border-white/10 rounded-2xl">
            No open requirements shared with you yet. When the Vinci trade desk broadcasts a matching RFQ, it will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {rows.map((r) => (
              <BroadcastCard key={r.id} row={r} onQuote={() => setOpen(r)} />
            ))}
          </div>
        )}
      </div>

      {open ? <QuoteModal row={open} onClose={() => setOpen(null)} onSubmit={(form) => submitQuote(open.id, form)} /> : null}
    </div>
  );
}

function BroadcastCard({ row, onQuote }) {
  const m = row.masked_rfq || {};
  const q = row.my_quote;
  return (
    <div data-testid={`bcast-${row.id}`} className="border border-white/10 rounded-2xl p-6 bg-white/[0.02] hover:border-[#B87333]/40 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk">{m.ref}</div>
          <div className="font-serif text-2xl mt-1">{m.material} {m.grade ? `· ${m.grade}` : ""}</div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-grotesk text-[#C9C9C2]">
          <EyeOff className="w-3 h-3" /> {m.buyer_alias}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs text-[#C9C9C2] mb-5">
        <Info label="Quantity" value={`${m.weight_tons || "—"} ${m.unit || "MT"}`} />
        <Info label="Destination" value={m.delivery_state || "—"} />
        <Info label="Target Delivery" value={m.target_delivery || "—"} />
        <Info label="Received" value={fmtDate(row.created_at)} />
      </div>
      {m.notes ? <div className="text-xs text-[#8A8A82] font-body mb-4 border-l-2 border-[#B87333]/40 pl-3 italic">{m.notes}</div> : null}

      {q ? (
        <div className="border border-[#2FB999]/30 bg-[#2FB999]/5 rounded-lg p-3 mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#2FB999] font-grotesk mb-1">Your quote</div>
          <div className="text-xs text-[#C9C9C2]">₹{q.price_per_unit?.toLocaleString?.("en-IN") || q.price_per_unit} / {q.unit} · Lead {q.lead_time || "—"} · Status: <span className="uppercase text-[#F5F5F0]">{(q.status || "").replace(/_/g, " ")}</span></div>
        </div>
      ) : null}

      <button data-testid={`bcast-quote-${row.id}`} onClick={onQuote}
        disabled={q?.status === "forwarded_to_buyer" || q?.status === "accepted"}
        className="w-full rounded-full bg-[#B87333] text-black px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
        <Send className="w-4 h-4" /> {q ? (q.status === "pending_admin_review" ? "Revise Quote" : "Locked") : "Submit Quote"}
      </button>
    </div>
  );
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] font-grotesk mb-1">{label}</div><div>{value}</div></div>;
}

function QuoteModal({ row, onClose, onSubmit }) {
  const existing = row.my_quote || {};
  const [form, setForm] = useState({
    price_per_unit: existing.price_per_unit || "",
    unit: existing.unit || (row.masked_rfq?.unit || "MT"),
    lead_time: existing.lead_time || "",
    payment_terms: existing.payment_terms || "",
    notes: existing.notes || "",
  });
  const bind = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const send = (e) => {
    e.preventDefault();
    if (!form.price_per_unit || Number(form.price_per_unit) <= 0) return;
    onSubmit({ ...form, price_per_unit: Number(form.price_per_unit) });
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={onClose}>
      <form onSubmit={send} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0D0D0D] border border-[#B87333]/40 rounded-2xl p-8">
        <div className="mb-6">
          <div className="overline text-[#B87333] mb-1">{row.masked_rfq?.ref}</div>
          <div className="font-serif text-2xl">{row.masked_rfq?.material} · {row.masked_rfq?.buyer_alias}</div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Field label="Price per unit (₹)" required><input data-testid="quote-price" type="number" value={form.price_per_unit} onChange={bind("price_per_unit")} className="input" /></Field>
          <Field label="Unit"><input data-testid="quote-unit" value={form.unit} onChange={bind("unit")} className="input" /></Field>
          <Field label="Lead time"><input data-testid="quote-lead" value={form.lead_time} onChange={bind("lead_time")} placeholder="e.g. 7-10 days" className="input" /></Field>
          <Field label="Payment terms"><input data-testid="quote-terms" value={form.payment_terms} onChange={bind("payment_terms")} placeholder="e.g. 30% advance" className="input" /></Field>
        </div>
        <Field label="Notes"><textarea data-testid="quote-notes" value={form.notes} onChange={bind("notes")} rows={3} className="input resize-none" /></Field>
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="rounded-full border border-white/20 px-5 py-2 text-xs uppercase tracking-[0.2em] font-grotesk">Cancel</button>
          <button data-testid="quote-submit" type="submit" className="rounded-full bg-[#B87333] text-black px-6 py-2 text-xs uppercase tracking-[0.2em] font-grotesk hover:brightness-110">Submit</button>
        </div>
        <style>{`.input{width:100%;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:.6rem .8rem;font-size:12px;color:#F5F5F0;outline:none;transition:border-color .2s}.input:focus{border-color:#B87333}`}</style>
      </form>
    </div>
  );
}
function Field({ label, required, children }) {
  return <label className="block mb-3"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-1.5">{label}{required ? " *" : ""}</div>{children}</label>;
}
